import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table, Select, message, Row, Col, Tag, Input } from 'antd';
import { withAppContext } from 'context/AppContext';
import { getUsers } from 'utils/apis/user';
import { getConstants } from 'utils/apis/constants';
import { getEInvoiceSubUsers, getEInvoiceSubUserOptions, createEInvoiceSubUser, deleteEInvoiceSubUser } from 'utils/apis/eInvoice';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const getHostId = userOrAuth => {
  if (!userOrAuth) return null;
  if (userOrAuth.hostId) return String(userOrAuth.hostId);
  const roleHost = userOrAuth.roles && userOrAuth.roles[0] && userOrAuth.roles[0].host;
  if (!roleHost) return null;
  return String(roleHost._id || roleHost);
};

const getUserContactRaw = user => {
  if (!user) return '';
  if (user.contactNos) {
    return Array.isArray(user.contactNos) ? user.contactNos[0] || '' : String(user.contactNos);
  }
  const fromProfile = user.userProfile && user.userProfile.contactNos && user.userProfile.contactNos[0];
  return fromProfile || '';
};

/** Prefill contact form from HP profile string; default country +60. */
const parseContactForForm = raw => {
  if (!raw) {
    return { countryCode: '+60', number: '' };
  }
  const cleaned = String(raw).replace(/\s/g, '');
  const withPlus = cleaned.match(/^(\+\d{1,4})(\d+)$/);
  if (withPlus) {
    return { countryCode: withPlus[1], number: withPlus[2] };
  }
  const digits = cleaned.replace(/\D/g, '');
  if (digits.startsWith('60') && digits.length > 9) {
    return { countryCode: '+60', number: digits.slice(2) };
  }
  if (digits.startsWith('0')) {
    return { countryCode: '+60', number: digits.slice(1) };
  }
  return { countryCode: '+60', number: digits };
};

const fieldLabelStyle = { marginBottom: 4, fontWeight: 500 };
const readOnlyValueStyle = {
  padding: '6px 11px',
  background: '#f5f5f5',
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  minHeight: 32,
  color: 'rgba(0,0,0,0.65)'
};

const SubUsersPanel = props => {
  const {
    eInvoiceUser,
    currentUser,
    canManage,
    hostId: hostIdProp,
    embedded = false,
    hideAddButton = false,
    addModalVisible: controlledAddModalVisible,
    onAddModalVisibleChange
  } = props;

  const [loading, setLoading] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [hostUsers, setHostUsers] = useState([]);
  const [hostUsersLoaded, setHostUsersLoaded] = useState(false);
  const [internalModalVisible, setInternalModalVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState(undefined);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [contactCountryCode, setContactCountryCode] = useState('+60');
  const [contactNumber, setContactNumber] = useState('');
  const [countryCodeOptions, setCountryCodeOptions] = useState([]);
  const [allowedProject, setAllowedProject] = useState([]);
  const [menuPermissions, setMenuPermissions] = useState(['eInvoice']);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [menuPermissionOptions, setMenuPermissionOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const isAddModalControlled = typeof controlledAddModalVisible === 'boolean';
  const modalVisible = isAddModalControlled ? controlledAddModalVisible : internalModalVisible;

  const resetModalState = () => {
    setStep(1);
    setSelectedUserId(undefined);
    setPassword('');
    setConfirmPassword('');
    setContactCountryCode('+60');
    setContactNumber('');
    setAllowedProject([]);
    setMenuPermissions(['eInvoice']);
    setCompanyOptions([]);
    setMenuPermissionOptions([]);
  };

  const setModalVisible = visible => {
    if (!visible) {
      resetModalState();
    }
    if (isAddModalControlled) {
      if (onAddModalVisibleChange) {
        onAddModalVisibleChange(visible);
      }
    } else {
      setInternalModalVisible(visible);
    }
  };

  const hostId = hostIdProp || getHostId(eInvoiceUser);
  // Avoid stale accountId from the previous host while GetEinvoiceUser is still in flight.
  const accountId = (() => {
    if (!eInvoiceUser || !eInvoiceUser.accountId) return undefined;
    if (hostIdProp && eInvoiceUser.hostId && String(eInvoiceUser.hostId) !== String(hostIdProp)) {
      return undefined;
    }
    return String(eInvoiceUser.accountId);
  })();

  const loadSubUsers = async () => {
    // Wait until eInvoiceUser matches the selected host before listing Team.
    if (hostIdProp && eInvoiceUser && eInvoiceUser.hostId && String(eInvoiceUser.hostId) !== String(hostIdProp)) {
      return;
    }
    if (hostIdProp && !eInvoiceUser) {
      return;
    }

    setLoading(true);
    try {
      const res = await getEInvoiceSubUsers({
        hostId: hostIdProp || undefined,
        accountId
      });
      if (res && res.status >= 200 && res.status < 300) {
        const list = Array.isArray(res.data) ? res.data : [];
        setSubUsers(list);
      } else if (res) {
        message.error((res.data && res.data.message) || 'Failed to load sub users');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHostUsers = async () => {
    if (hostUsersLoaded) return;
    const res = await getUsers();
    if (res && res.status >= 200 && res.status < 300) {
      setHostUsers(Array.isArray(res.data) ? res.data : []);
      setHostUsersLoaded(true);
    }
  };

  const loadCreateOptions = async selected => {
    setOptionsLoading(true);
    try {
      const [optionsRes, countriesRes] = await Promise.all([
        getEInvoiceSubUserOptions({
          hostId: hostIdProp || undefined,
          accountId
        }),
        getConstants('countries')
      ]);

      if (optionsRes && optionsRes.status >= 200 && optionsRes.status < 300) {
        const companies = (optionsRes.data && optionsRes.data.companies) || [];
        const menus = (optionsRes.data && optionsRes.data.menuPermissions) || [];
        setCompanyOptions(companies);
        setMenuPermissionOptions(menus);
        setAllowedProject(companies.map(c => c.value));
        if (!menus.some(m => m.value === 'eInvoice') && menus.length) {
          setMenuPermissions([menus[0].value]);
        } else {
          setMenuPermissions(['eInvoice']);
        }
      } else {
        message.error((optionsRes && optionsRes.data && optionsRes.data.message) || 'Failed to load permission options');
      }

      if (countriesRes && countriesRes.status === 200 && countriesRes.data) {
        const seen = new Set();
        const phoneOptions = [];
        Object.keys(countriesRes.data).forEach(k => {
          const country = countriesRes.data[k];
          const code = country && country.phoneCode;
          if (code && !seen.has(code)) {
            seen.add(code);
            phoneOptions.push({ value: code, label: code });
          }
        });
        phoneOptions.sort((a, b) => String(a.value).localeCompare(String(b.value)));

        const parsed = parseContactForForm(getUserContactRaw(selected));
        if (parsed.countryCode && !seen.has(parsed.countryCode)) {
          phoneOptions.unshift({ value: parsed.countryCode, label: parsed.countryCode });
        }
        setCountryCodeOptions(phoneOptions);
        setContactCountryCode(parsed.countryCode || '+60');
        setContactNumber(String(parsed.number || '').replace(/\D/g, ''));
      } else {
        const parsed = parseContactForForm(getUserContactRaw(selected));
        setContactCountryCode(parsed.countryCode || '+60');
        setContactNumber(String(parsed.number || '').replace(/\D/g, ''));
      }
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    loadSubUsers();
  }, [hostIdProp, accountId, eInvoiceUser && eInvoiceUser.hostId]);

  useEffect(() => {
    if (modalVisible && canManage) {
      loadHostUsers();
    }
    if (!modalVisible) {
      resetModalState();
    }
  }, [modalVisible, canManage]);

  const linkedUserIds = useMemo(() => {
    const ids = new Set(subUsers.map(s => String(s.userId)));
    if (currentUser && currentUser._id) {
      ids.add(String(currentUser._id));
    }
    return ids;
  }, [subUsers, currentUser]);

  const candidateOptions = useMemo(() => {
    return hostUsers
      .filter(u => {
        if (!u || !u._id) return false;
        if (linkedUserIds.has(String(u._id))) return false;
        const userHostId = getHostId(u);
        return hostId && userHostId && userHostId === hostId;
      })
      .map(u => {
        const first = (u.userProfile && u.userProfile.firstName) || '';
        const last = (u.userProfile && u.userProfile.lastName) || '';
        const name = `${first} ${last}`.trim();
        return {
          value: String(u._id),
          label: name ? `${name} (${u.email})` : u.email || String(u._id)
        };
      });
  }, [hostUsers, linkedUserIds, hostId]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return hostUsers.find(u => String(u._id) === String(selectedUserId)) || null;
  }, [hostUsers, selectedUserId]);

  const handleNext = async () => {
    if (!selectedUserId) {
      message.warning('Select a user to add');
      return;
    }
    const selected = hostUsers.find(u => String(u._id) === String(selectedUserId)) || null;
    setStep(2);
    await loadCreateOptions(selected);
  };

  const handleCreate = async () => {
    if (!selectedUserId) {
      message.warning('Select a user to add');
      return;
    }
    if (!contactCountryCode) {
      message.warning('Select a country code');
      return;
    }
    if (!contactNumber || !/^\d+$/.test(contactNumber)) {
      message.warning('Enter a phone number (digits only)');
      return;
    }
    if (!PASSWORD_REGEX.test(password)) {
      message.warning('Password must be at least 8 characters and include 1 digit, 1 uppercase and 1 lowercase letter');
      return;
    }
    if (password !== confirmPassword) {
      message.warning('Passwords do not match');
      return;
    }
    if (!allowedProject.length) {
      message.warning('Select at least one company');
      return;
    }
    if (!menuPermissions.length) {
      message.warning('Select at least one menu permission');
      return;
    }

    setSaving(true);
    try {
      const res = await createEInvoiceSubUser({
        targetUserId: selectedUserId,
        password,
        contact: {
          countryCode: contactCountryCode,
          number: contactNumber
        },
        allowedProject,
        menuPermissions,
        ...(hostIdProp ? { hostId: hostIdProp } : {}),
        ...(accountId ? { accountId } : {})
      });
      if (res && res.status >= 200 && res.status < 300) {
        message.success('Sub user added');
        setModalVisible(false);
        await loadSubUsers();
      } else {
        message.error((res && res.data && res.data.message) || 'Failed to add sub user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!removeTarget || !removeTarget._id) return;
    setRemoving(true);
    try {
      const res = await deleteEInvoiceSubUser(removeTarget._id, {
        hostId: hostIdProp || undefined,
        accountId
      });
      if (res && res.status >= 200 && res.status < 300) {
        message.success('Sub user removed');
        setRemoveTarget(null);
        await loadSubUsers();
      } else {
        message.error((res && res.data && res.data.message) || 'Failed to remove sub user');
      }
    } finally {
      setRemoving(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => {
        const first = record.user && record.user.firstName;
        const last = record.user && record.user.lastName;
        const full = `${first || ''} ${last || ''}`.trim();
        return full || record.name || '—';
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: text => text || '—'
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => (record && record.isPrimary ? <Tag color="blue">Primary User</Tag> : <Tag color="cyan">Sub User</Tag>)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const value = String(status || '').toLowerCase();
        if (value === 'active') {
          return <Tag color="green">Active</Tag>;
        }
        if (value === 'inactive' || value === 'deleted' || value === 'disabled') {
          return <Tag color="red">{status}</Tag>;
        }
        if (value === 'cancelled' || value === 'canceled') {
          return <Tag color="orange">Cancelled</Tag>;
        }
        return status ? <Tag>{status}</Tag> : '—';
      }
    },
    canManage
      ? {
          title: 'Actions',
          key: 'actions',
          width: 120,
          render: (_, record) =>
            record.isPrimary ? (
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>—</span>
            ) : (
              <Button type="link" style={{ color: '#ff4d4f', padding: 0 }} onClick={() => setRemoveTarget(record)}>
                Remove
              </Button>
            )
        }
      : null
  ].filter(Boolean);

  const modalFooter =
    step === 1
      ? [
          <Button key="cancel" onClick={() => setModalVisible(false)} disabled={saving}>
            Cancel
          </Button>,
          <Button key="next" type="primary" onClick={handleNext}>
            Next
          </Button>
        ]
      : [
          <Button key="back" onClick={() => setStep(1)} disabled={saving || optionsLoading}>
            Back
          </Button>,
          <Button key="create" type="primary" loading={saving} disabled={optionsLoading} onClick={handleCreate}>
            Create
          </Button>
        ];

  return (
    <div style={embedded ? undefined : { marginTop: 32 }}>
      {!embedded && (
        <Row type="flex" justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Sub users</div>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>Let host employees access this e-Invoice account in Host Platform.</div>
          </Col>
          {canManage && !hideAddButton && (
            <Col>
              <Button type="primary" icon="plus" onClick={() => setModalVisible(true)}>
                Add Sub User
              </Button>
            </Col>
          )}
        </Row>
      )}

      <Table
        rowKey={record => record._id}
        loading={loading}
        columns={columns}
        dataSource={subUsers}
        pagination={false}
        locale={{ emptyText: 'No sub users yet' }}
      />

      <Modal
        title={step === 1 ? 'Add Sub User for e-Invoice Dashboard' : 'Add Sub User for e-Invoice Dashboard — Menu Permissions'}
        visible={modalVisible}
        onCancel={() => {
          if (!saving) {
            setModalVisible(false);
          }
        }}
        footer={modalFooter}
        confirmLoading={saving}
        destroyOnClose
        width={step === 2 ? 560 : 520}
      >
        {step === 1 ? (
          <>
            <div style={{ marginBottom: 8 }}>Select a Host Platform user on this host:</div>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Search user"
              value={selectedUserId}
              onChange={setSelectedUserId}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option.props.children)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {candidateOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Full name</div>
              <div style={readOnlyValueStyle}>
                {!selectedUser
                  ? '—'
                  : `${(selectedUser.userProfile && selectedUser.userProfile.firstName) || ''} ${(selectedUser.userProfile &&
                      selectedUser.userProfile.lastName) ||
                      ''}`.trim() ||
                    selectedUser.username ||
                    selectedUser.email ||
                    '—'}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Email</div>
              <div style={readOnlyValueStyle}>{(selectedUser && selectedUser.email) || '—'}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Contact</div>
              <Row gutter={8}>
                <Col span={8}>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    value={contactCountryCode}
                    onChange={setContactCountryCode}
                    placeholder="Code"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option.props.children)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {countryCodeOptions.map(opt => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={16}>
                  <Input
                    value={contactNumber}
                    onChange={e => setContactNumber(String(e.target.value || '').replace(/\D/g, ''))}
                    placeholder="Phone number"
                    inputMode="numeric"
                  />
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Password</div>
              <Input.Password value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" />
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4 }}>
                Require at least 1 digit, 1 upper case, 1 lower case and at least 8 characters
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Repeat password</div>
              <Input.Password value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" />
            </div>

            <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0 16px', paddingTop: 16, fontWeight: 600 }}>Permission Controls</div>

            <div style={{ marginBottom: 16 }}>
              <div style={fieldLabelStyle}>Assign Company</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select company"
                value={allowedProject}
                onChange={setAllowedProject}
                loading={optionsLoading}
                optionFilterProp="children"
              >
                {companyOptions.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4 }}>
                If you select multiple company, user will have access to all selected company.
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={fieldLabelStyle}>Menu Permission</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select menu permission"
                value={menuPermissions}
                onChange={setMenuPermissions}
                loading={optionsLoading}
                optionFilterProp="children"
              >
                {menuPermissionOptions.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4 }}>
                If you select Usage, user will have access to Usage in the menu.
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Remove sub user"
        visible={!!removeTarget}
        onCancel={() => {
          if (!removing) {
            setRemoveTarget(null);
          }
        }}
        onOk={handleDelete}
        okText="Remove"
        okType="danger"
        confirmLoading={removing}
        destroyOnClose
      >
        <p style={{ marginBottom: 0 }}>
          Remove <strong>{(removeTarget && (removeTarget.email || removeTarget.name)) || 'this sub user'}</strong>? They will lose access to this
          e-Invoice account in Host Platform
          {removeTarget && removeTarget.isSubUser ? ' and their e-Invoice sub user account will be deleted.' : '.'}
        </p>
      </Modal>
    </div>
  );
};

export default withAppContext(SubUsersPanel);
